import { TurnstileInstance } from '@marsidev/react-turnstile';
import { Stack, Typography, Button } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import { CheckCircleIcon } from '@/assets/icons/CheckCircleIcon';
import { useAuth } from '@/context/AuthProvider';

import ResetModalWithForm from '@/components/auth/ResetModalWithForm';
import ResetPageWrapper from '@/components/auth/ResetPageWrapper';
import FormCaptcha from '@/components/form/FormCaptcha';
import FormTextField from '@/components/form/FormTextField';

interface FormData {
  email: string;
  captchaToken: string;
}

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ isOpen, onClose }) => {
  const { resetPasswordForEmail } = useAuth();
  const turnstileRef = useRef<TurnstileInstance>();
  const form = useForm<FormData>();
  const {
    control,
    register,
    formState: { errors, isSubmitting },
  } = form;

  const [isEmailSent, setIsEmailSent] = useState(false);

  const handleClose = () => {
    setIsEmailSent(false);
    turnstileRef.current?.reset();
    onClose();
  };

  const onSubmit = async (data: FormData) => {
    if (isEmailSent) {
      handleClose();
      return;
    }

    const { email, captchaToken } = data;

    const { error } = await resetPasswordForEmail(email, captchaToken);

    if (error) {
      turnstileRef.current?.reset();
      throw error;
    }

    setIsEmailSent(true);
  };

  if (!isOpen) return null;

  return (
    <ResetModalWithForm
      title=""
      buttonLabel=""
      maxWidth="sm"
      form={form}
      onClose={handleClose}
      onSubmit={onSubmit}
      showCloseButton={false}
      PaperProps={{
        sx: {
          width: '500px',
          height: '600px',
          maxHeight: '90vh',
        },
      }}
    >
      {isEmailSent ? (
        <Stack
          spacing={6.5}
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          sx={{ maxWidth: 420, mx: 'auto', mt: 6 }}
        >
          <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
          <Typography variant="h4" fontWeight={600}>
            Sent Reset Email
          </Typography>

          <Typography variant="body1">
            We've sent a password reset link to your email address. Please check your inbox and
            follow the instructions to reset your password.
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 360 }}>
            If you don't see the email in your inbox, please check your spam folder or promotions
            tab.
          </Typography>
        </Stack>
      ) : (
        <ResetPageWrapper>
          <Stack spacing={3} width="100%" alignItems="center">
            <Stack spacing={1.5} mb={4} textAlign="center" alignItems="center">
              <Typography variant="h3">Forgot Password?</Typography>
              <Typography variant="body2">
                No worries, we'll send you reset instructions.
              </Typography>
            </Stack>

            <FormTextField
              label="Email"
              placeholder="Enter your email"
              textFieldProps={{
                autoComplete: 'email',
                autoFocus: true,
                type: 'email',
              }}
              error={!!errors.email}
              helperText={errors.email?.message?.toString()}
              control={control}
              required
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email address',
                },
              })}
            />

            <FormCaptcha
              control={control}
              error={!!errors.captchaToken}
              helperText={errors.captchaToken?.message?.toString()}
              required
              {...register('captchaToken', {
                required: 'Captcha Token is required',
              })}
              turnstileRef={turnstileRef}
            />

            <Stack spacing={1} mb={1} width="100%">
              <LoadingButton loading={isSubmitting} fullWidth variant="contained" type="submit">
                Send reset email
              </LoadingButton>
              <Button fullWidth variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </ResetPageWrapper>
      )}
    </ResetModalWithForm>
  );
};

export default ResetPasswordModal;
